import { CancelToken, isCancel } from "axios";

const getDefaultState = () => {
  return {
    // The Profile Qualifications currently visible
    active: [],
    // What filters are currently applied
    filters: {},
    // Used to indicate when waiting on the server
    loading: false,
    // The current page being viewed
    page: 1,
    // Size of current page beign viewed
    rowsPerPage: 25,
    // The search string being applied
    search: "",
    // Selected candidates used for bulk actions
    selected: [],
    // Sorting
    sort: {
      by: "profile.updatedAt",
      descending: true,
    },
    // Token source used to abandon requests
    source: null,
    // The total records matching the filter according to the server (for pagination)
    total: 0,
  };
};

const state = getDefaultState();

const getters = {
  active: (state) => state.active,
  find: (state) => (id) =>
    state.active.find((eligibility) => eligibility.id == id),
  hasIncomplete: (state) =>
    state.active.some((eligibility) => !eligibility.complete),
  loading: (state) => state.loading,
  selected: (state) => state.selected,
};

const mutations = {
  setActive(
    state,
    {
      items = [],
      page = 1,
      per = 25,
      total = 0,
      search = null,
      sort = { by: "updatedAt", descending: true },
      filters = {},
    }
  ) {
    state.search = search;
    state.active = items;
    state.sort = sort;
    state.filters = { ...filters, status: state.filters.status };
    state.page = page;
    state.rowsPerPage = per;
    state.total = total;
    state.loading = false;
    state.selected = [];
  },
  set(state, eligibility) {
    const find = state.active.find((o) => o.id == eligibility.id);
    if (find) state.active.splice(state.active.indexOf(find), 1, eligibility);
    if (!find) state.active.push(eligibility);
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  select(state, id) {
    if (Array.isArray(id)) state.selected = id;
    else state.selected.push(id);
  },
  deselect(state, id) {
    state.selected = state.selected.filter((s) => s !== id);
  },
  clearSelected(state) {
    state.selected = [];
  },
};

const actions = {
  abandon({ state }, reason) {
    if (state.source) {
      state.source.cancel(reason);
      state.source = null;
      state.loading = false;
    }
  },

  resetState({ commit }) {
    commit("resetState");
  },

  search(
    { commit, state },
    {
      applied = false,
      descending,
      eligible = true,
      filters,
      opportunity = Vue.$route.params.id,
      page,
      search,
      sortBy,
      rowsPerPage,
    } = {}
  ) {
    // Cancel previous search actions
    if (state.source) {
      state.source.cancel();
      state.loading = false;
    }
    state.source = CancelToken.source();
    descending =
      typeof descending === "boolean" ? descending : state.sort.descending;
    state.filters = filters || state.filters;
    page = page || state.page;
    const per = rowsPerPage || state.rowsPerPage;
    search = search || state.search;
    sortBy = sortBy || state.sort.by;
    // Candidate filters sets sortBy to completion only
    sortBy =
      sortBy.toLowerCase() === "completion" ? "profile.completion" : sortBy;
    const sort = `${sortBy} ${descending ? "DESC" : "ASC"}`;

    state.loading = true;
    return Vue.$http
      .get(`client/opportunities/${opportunity}/eligibilities`, {
        params: {
          ...state.filters,
          page,
          search,
          sort,
          per,
          applied,
          eligible,
        },
        cancelToken: state.source.token,
      })
      .then(
        (response) => commit("setActive", response.data),
        (errors) => {
          if (!isCancel(errors)) state.loading = false;
        }
      );
  },

  updateFilters({ dispatch, state }, filters) {
    Object.assign(state.filters, { ...filters });
    // Remove completed and referred filters if all applications are required
    if (state.filters["complete"] == false) delete state.filters["complete"];
    if (state.filters["referred"] == false) delete state.filters["referred"];
    state.page = 1;
    return dispatch("search").then(() =>
      this.dispatch("client/opportunities/fetchCounts", {
        filters: state.filters,
      })
    );
  },

  fetch({ commit, getters }, id) {
    const eligibility = getters.find(id);

    if (eligibility) {
      commit("set", Object.assign(eligibility, { loading: true }));
    } else {
      commit("set", { id, loading: true });
    }

    return Vue.$http
      .get(`client/opportunities/${Vue.$route.params.id}/eligibilities/${id}`)
      .then((response) => {
        commit("set", Object.assign(response.data, { loading: false }));
        return response;
      });
  },
  invite(store, eligibilityIds) {
    if (Array.isArray(eligibilityIds)) {
      return Vue.$http.post(
        `client/opportunities/${Vue.$route.params.id}/eligibilities/invite`,
        {
          eligibilityIds,
        }
      );
    }
    return Vue.$http.patch(
      `client/opportunities/${Vue.$route.params.id}/eligibilities/${parseInt(
        eligibilityIds
      )}/invite`
    );
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true,
};
